import { useState, useEffect, useRef } from 'react';
import { WalletStatus } from '@/components/ui/WalletStatus';
import { NotificationBell } from '@/components/ui/NotificationBell';

interface HeaderProps {
  onShowNetworkModal?: () => void;
}

export function Header({ onShowNetworkModal }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Find the scrollable container (the one with overflow-auto in App.tsx)
    const findScrollContainer = () => {
      const container = document.querySelector('.flex-1.overflow-auto.relative');
      if (container) {
        scrollContainerRef.current = container as HTMLElement;
        return container;
      }
      return null;
    };

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const scrollPosition = container.scrollTop;
        setIsScrolled(scrollPosition > 10);
      } else {
        // Fallback to window scroll
        const scrollPosition = window.scrollY || document.documentElement.scrollTop;
        setIsScrolled(scrollPosition > 10);
      }
    };

    // Try to find the container
    const container = findScrollContainer();
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Check initial scroll position
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div 
      className={`sticky top-0 left-0 right-0 z-50 flex justify-end p-2 sm:p-4 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/20 backdrop-blur-xl border-b border-white/10' 
          : 'bg-transparent'
      }`}
      style={isScrolled ? {
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      } : {}}
    >
      <div className={`flex items-center gap-2 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-sm' : ''
      }`}>
        <NotificationBell />
        <WalletStatus onShowNetworkModal={onShowNetworkModal} />
      </div>
    </div>
  );
}
